import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal/Modal';
import Btn from '../Btn/Btn';
import { TaskSummary } from './types';
import { QuestionnaireKind, isTherapyMaterialKind } from '../../types/QuestionnaireKind';
import './DeleteQuestionnaireModal.css';
import './DeleteTaskModal.css';

type Props = {
    task: TaskSummary | null;
    isDeleting: boolean;
    error: string | null;
    onClose: () => void;
    onDelete: () => Promise<void>;
    resourceKind: QuestionnaireKind;
};

const DeleteTaskModal = ({ task, isDeleting, error, onClose, onDelete, resourceKind }: Props): JSX.Element | null => {
    const { t } = useTranslation();
    const isTherapyMaterial = isTherapyMaterialKind(resourceKind);
    const [confirmationName, setConfirmationName] = useState('');
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        // Reset state when modal opens/closes
        if (task) {
            setConfirmationName('');
            setValidationError(null);
        }
    }, [task]);

    if (!task) {
        return null;
    }

    const questionnaireNameToConfirm = String(
        task.questionnaireName ?? task.questionnaireId ?? t('Unknown') ?? '',
    ).trim();

    const handleDelete = async (): Promise<void> => {
        if (confirmationName.trim() !== questionnaireNameToConfirm) {
            setValidationError(
                isTherapyMaterial
                    ? t('The therapy material name does not match.')
                    : t('The questionnaire name does not match.'),
            );
            return;
        }

        setValidationError(null);
        await onDelete();
    };

    const isConfirmationValid = confirmationName.trim() === questionnaireNameToConfirm;

    return (
        <Modal title={isTherapyMaterial ? t('Delete Therapy Material Assignment') : t('Delete Task')} close={onClose}>
            <div className="frontpage__delete-task-form">
                <p>
                    <strong>{t('Are you sure you want to delete this task?')}</strong>
                </p>
                <div className="frontpage__warning-box">
                    <p>
                        {isTherapyMaterial
                            ? t(
                                  'Assignments with responses cannot be deleted. Therapy material responses are never deleted.',
                              )
                            : t('Tasks with responses cannot be deleted. Questionnaire Responses are never deleted.')}
                    </p>
                </div>
                <div className="frontpage__assign-details">
                    <div>
                        <strong>{t('Patient')}</strong>
                    </div>
                    <div>{task.patientName || task.patientId || t('Unknown')}</div>
                </div>
                <div className="frontpage__assign-details">
                    <div>
                        <strong>{isTherapyMaterial ? t('Therapy Material') : t('Questionnaire')}</strong>
                    </div>
                    <div>{questionnaireNameToConfirm}</div>
                </div>
                <div className="frontpage__confirmation-section">
                    <p>
                        {isTherapyMaterial
                            ? t('To confirm deletion, please enter the therapy material name below:')
                            : t('To confirm deletion, please enter the questionnaire name below:')}
                    </p>
                    <input
                        type="text"
                        className="frontpage__confirmation-input"
                        placeholder={
                            isTherapyMaterial
                                ? t('Enter therapy material name to confirm')
                                : t('Enter questionnaire name to confirm')
                        }
                        value={confirmationName}
                        onChange={(e) => {
                            setConfirmationName(e.target.value);
                            setValidationError(null);
                        }}
                        disabled={isDeleting}
                        autoFocus
                    />
                </div>
                {validationError && <div className="frontpage__error">{validationError}</div>}
                {error && <div className="frontpage__error">{error}</div>}
                <div className="frontpage__assign-actions">
                    <button
                        type="button"
                        className="regular-btn frontpage__delete-btn"
                        onClick={handleDelete}
                        disabled={isDeleting || !isConfirmationValid}
                    >
                        {isDeleting
                            ? isTherapyMaterial
                                ? t('Deleting assignment...')
                                : t('Deleting task...')
                            : t('Delete')}
                    </button>
                    <Btn title={t('Cancel')} variant="secondary" onClick={onClose} disabled={isDeleting} />
                </div>
            </div>
        </Modal>
    );
};

export default DeleteTaskModal;
